import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  DropDownInput,
  InputField,
  OptionGroup,
  RadioInput,
  DateInput,
  MiniTabs,
  DataTable,
} from "@/components";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { BsBoxes } from "react-icons/bs";
import { HiOutlineUpload } from "react-icons/hi";
import { RiFileExcel2Line } from "react-icons/ri";

import ColumnHelper from "@/utils/ColumnHelper";
import {
  useFilter,
  usePersistentFilters,
  useAlert,
  useConfirmationDialog,
  useApi,
} from "@/";
import { NDRHistory, NDRUploads } from "..";
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { fetchNDRData, selectAllNDRs } from "../../app/Slices";
import { subDays } from "date-fns";
import { MdOutlineSync } from "react-icons/md";

// Predefined options to avoid duplication

const paymentOptions = [
  { value: "Prepaid", label: "Prepaid" },
  { value: "COD", label: "COD" },
];

const bulkOptions = [
  { label: "Bulk NDR", value: "bulk_ndr", icon: <BsBoxes /> },
];

const Ndr = () => {
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  const { callApi } = useApi();
  const { alert, AlertDialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();

  // General state
  const [activeTab, setActiveTab] = useState("all");
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  // Initial filters
  const [filters, setFilters] = usePersistentFilters({
    customerName: "",
    customerId: "",
    orderId: "",
    productName: "",
    channelName: "", // ✅ Keep the correct key
    courierName: "", // ✅ Match filterConfig key
    shippingId: "",
    paymentMode: "",
    awbId: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);

  const courier = useSelector((state) => state.app?.appData?.courier) || [];
  const channel = useSelector((state) => state.app?.appData?.channels) || [];
  const dispatch = useDispatch();
  const fetchData = async () => {
    const response = await dispatch(
      fetchNDRData({
        startDate: filters.date.from,
        endDate: filters.date.to,
      }),
    );
  };
  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);

  const NDRs = useSelector(selectAllNDRs) || [];
  // General filter configuration.
  const filterConfig = useMemo(
    () => ({
      customerName: { type: "partial", itemKey: "user.name" },
      customerId: {
        type: "partial",
        itemKey: "user.clientId",
      },
      orderId: {
        type: "comma-api",
        itemKey: "userOrderId",
        model: "Shipment",
        endpoint: "/common/search",
      },
      productName: { type: "partial", itemKey: "productName" },
      shippingId: {
        type: "comma",
        itemKey: "shipmentId",
      },
      awbId: {
        type: "comma-api",
        itemKey: "awbNumber",
        model: "Shipment",
        endpoint: "/common/search",
      },
      channelName: { type: "exact", itemKey: "order.channel" },
      courierName: { type: "exact", itemKey: "courierId" },
      paymentMode: { type: "exact", itemKey: "paymentMode" },
      date: { type: "date", itemKey: "ndrRecords.ndrDate" },
    }),
    [],
  );

  // Tab filter configuration as a serializable object.
  const tabConfig = useMemo(
    () => ({
      idKey: "ndrStatus",
      allTabId: "all",
      filters: {
        actionRequired: { type: "exact", value: "Exception" },
        actionRequested: { type: "exact", value: "Resolved" },
        delivered: { type: "exact", value: "Delivered" },
        rto: { type: "exact", value: "RTO" },
        // Additional tabs can be added here.
      },
    }),
    [],
  );

  // Use the combined asynchronous filtering hook.
  const {
    data: finalFilteredData,
    counts,
    isLoading,
  } = useFilter(NDRs, appliedFilters, filterConfig, activeTab, tabConfig);

  const courierOptions = useMemo(
    () => courier.map((c) => ({ value: c._id, label: c.name })),
    [courier],
  );
  const channelOptions = useMemo(
    () => channel.map((c) => ({ value: c.name, label: c.name })),
    [channel],
  );
  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const statusStyle = {
    Pending: "bg-yellow-100/90 text-yellow-800",
    Pickup: "bg-yellow-100/90 text-yellow-800",
    "In Transit": "bg-orange-100 text-orange-800",
    Rescheduled: "bg-orange-100 text-orange-800",
    "Out for Delivery": "bg-purple-200/80 text-purple-800",
    Delivered: "bg-green-300/25 text-green-800",
    "Partially Delivered": "bg-green-300/25 text-green-800",
    Cancelled: "bg-cyan-800/20 text-cyan-800",
    Lost: "bg-slate-900/20 text-slate-900",
    Damaged: "bg-amber-900/20 text-amber-900",
    "RTO All": "bg-red-300/30 text-red-800",
    Exception: "bg-red-300/30 text-red-800",
    RTO: "bg-red-300/30 text-red-800",
    "RTO In-Transit": "bg-orange-100 text-orange-800",
    "RTO Delivered": "bg-green-100 text-green-800",
    "RTO Lost": "bg-slate-900/20 text-slate-900",
  };
  const options = useMemo(
    () => [
      {
        label: "NDR Update",
        value: "ndr_update",
        icon: <MdOutlineSync />,
      },
    ],
    [],
  );

  // Table columns.
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "user?.name",
        meta: {
          tooltip: (row) => row.user?.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer Id",
        accessorKey: "user?.clientId",
        meta: {
          tooltip: (row) => row.user?.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Channel",
        accessorKey: "order?.channel",
        meta: {
          tooltip: (row) => row.order?.channel || "N/A",
        },
      }),
      ColumnHelper({
        header: "Order ID",
        accessorKey: "userOrderId",
        meta: {
          tooltip: (row) => row.userOrderId,
        },
      }),
      ColumnHelper({
        header: "Booking Date",
        accessorKey: "shipmentDate",
        cell: (info) => formatDate(info.row.original.shipmentDate),
        meta: {
          tooltip: (row) => formatDate(row.shipmentDate),
        },
      }),
      ColumnHelper({
        header: "Payment",
        accessorKey: "totalCharge",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalCharge)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalCharge),
        },
      }),
      ColumnHelper({
        header: "Payment Mode",
        accessorKey: "paymentMode",
        meta: {
          tooltip: (row) => row.paymentMode,
        },
      }),
      ColumnHelper({
        header: "Customer",
        accessorKey: "deliveryAddress.customerName",
        meta: {
          tooltip: (row) => row.deliveryAddress.customerName,
        },
      }),
      ColumnHelper({
        header: "Courier",
        accessorKey: "courierId",
        cell: (info) =>
          courier.find((c) => c._id === info.row.original.courierId)?.name,
        meta: {
          tooltip: (row) => courier.find((c) => c._id === row.courierId)?.name,
        },
      }),
      ColumnHelper({
        header: "AWB",
        accessorKey: "awbNumber",
        meta: {
          tooltip: (row) => row.awbNumber || "N/A",
        },
      }),
      ColumnHelper({
        header: "Shipping ID",
        accessorKey: "shipmentId",
        meta: {
          tooltip: (row) => row.shipmentId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Shipment Tracking",
        accessorKey: "status",
        cell: (info) => (
          <span
            className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
              statusStyle[info.row.original.status]
            }`}
          >
            {info.row.original.status}
          </span>
        ),
        meta: {
          tooltip: (row) => row.status,
        },
      }),
      ColumnHelper({
        header: "NDR Status",
        accessorKey: "ndrStatus",
        cell: (info) => {
          const status =
            info.row.original.ndrStatus === "Resolved"
              ? "Action Requested"
              : info.row.original.ndrStatus === "Exception"
                ? "Action Required"
                : info.row.original.ndrStatus;
          return (
            <span
              className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
                statusStyle[info.row.original.ndrStatus]
              }`}
            >
              {status}
            </span>
          );
        },
        meta: {
          tooltip: (row) =>
            row.ndrStatus === "Resolved"
              ? "Action Requested"
              : row.ndrStatus === "Exception"
                ? "Action Required"
                : row.ndrStatus,
        },
      }),
      ColumnHelper({
        header: "Exception Info",
        accessorKey: "exceptionInfo",
        cell: (info) => {
          const records = info.row.original.ndrRecords || [];
          const allInteractions = records.flatMap((record) =>
            (record.interactions || []).map((interaction) => ({
              ...interaction,
              attempt: record.attempt,
            })),
          );
          return (
            <AlertDialog key={info.getValue()}>
              <AlertDialogTrigger asChild>
                <p className="w-full text-ternary/80 truncate line-clamp-1 underline cursor-pointer">
                  {info.row.original.ndrRecords?.[
                    info.row.original.ndrRecords.length - 1
                  ]?.lastCommunication?.message || "No Exception Info"}
                </p>
              </AlertDialogTrigger>
              <AlertDialogContent
                aria-describedby=""
                className="w-[60%] max-w-[60%] p-0 gap-1 bg-background max-h-[80vh] overflow-y-auto"
              >
                <AlertDialogHeader>
                  <AlertDialogTitle className="px-2 pt-2 text-headline4 font-nunito text-secondary/80 font-bold">
                    NDR History
                  </AlertDialogTitle>
                  <div className="!overflow-y-auto max-h-[60vh]">
                    <NDRHistory data={allInteractions || []} />
                  </div>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-secondary/10 hover:bg-secondary/10 rounded-xl mr-2 mb-2 text-secondary/90">
                    Cancel
                  </AlertDialogCancel>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        },
        meta: {
          tooltip: (row) => {
            return (
              row.ndrRecords?.[row.ndrRecords.length - 1]?.lastCommunication
                ?.message || "No Exception Info"
            );
          },
        },
      }),
    ],
    [courier, formatCurrency, formatDate],
  );

  // Build tabs using counts returned by the worker.
  const tabs = useMemo(
    () => [
      { id: "all", label: "All", count: counts.all || 0 },
      {
        id: "actionRequired",
        label: "Action Required",
        count: counts.actionRequired || 0,
      },
      {
        id: "actionRequested",
        label: "Action Requested",
        count: counts.actionRequested || 0,
      },
      {
        id: "delivered",
        label: "Delivered",
        count: counts.delivered || 0,
      },
      {
        id: "rto",
        label: "RTO",
        count: counts.rto || 0,
      },
    ],
    [counts],
  );

  const handleChange = useCallback(
    (key, value) => {
      setFilters({ [key]: value });
    },
    [setFilters],
  );

  const handleDateChange = useCallback(
    (dateRange) => {
      setFilters({
        date: { from: dateRange?.from || "", to: dateRange?.to || "" },
      });
    },
    [setFilters],
  );

  const handleApply = useCallback(() => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const initialFilters = {
      customerName: "",
      customerId: "",
      orderId: "",
      productName: "",
      channelName: "",
      awbId: "",
      shippingId: "",
      courierName: "",
      paymentMode: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, [setFilters, startOfMonthDate, endOfMonthDate]);

  const handleTabChange = useCallback((tab) => {
    console.log("Tab Changed:", tab);
    setActiveTab(tab);
  }, []);
  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);

    if (value === "ndr_update") {
      handleNdrUpdate();
    }
  };
  const handleNdrUpdate = async () => {
    if (selectedRows.length === 0) {
      await alert("Please select at least one row.");
      return;
    }

    const shipmentIds = selectedRows
      .map((row) => row.shipmentId)
      .filter((id) => id && id.trim() !== "");

    if (shipmentIds.length === 0) {
      await alert("No valid shipment IDs found in selected rows.");
      return;
    }

    try {
      await confirm(
        "Are you sure you want to update NDR status for selected shipments?",
      );
    } catch {
      return;
    }

    console.log("Due update requested for shipments:", shipmentIds);
    try {
      const response = await callApi("/admin/updateNdrStatus", "POST", {
        shipmentIds: shipmentIds,
      });
      if (response?.success) {
        console.log("NDR update successful");
        await alert("NDR status updated successfully.");
        fetchData();
      } else {
        await alert(response?.message || "Failed to update NDR status.");
      }
    } catch (error) {
      console.error("Error updating NDR status:", error);
      await alert("An error occurred while updating NDR status.");
    }
  };

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header Section */}
        <div className="w-full flex flex-col gap-2 md:flex-row justify-between">
          <h2 className="text-headline3 lg:text-headline2 text-secondary/95 font-nunito font-bold">
            NDR Tracking
          </h2>
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button size="sm" icon={HiOutlineUpload}>
                Upload NDRs
              </Button>
            </SheetTrigger>
            <SheetContent aria-describedby="">
              <SheetHeader>
                <SheetTitle>Upload NDRs</SheetTitle>
              </SheetHeader>
              <NDRUploads setSheetOpen={setSheetOpen} />
            </SheetContent>
          </Sheet>
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            placeholder="Select From & To Date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Order ID"
            placeholder="Order IDs Comma Separated"
            value={filters.orderId}
            onChange={(value) => handleChange("orderId", value)}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name "
            value={filters.customerName}
            onChange={(value) => handleChange("customerName", value)}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID "
            value={filters.customerId}
            onChange={(value) => handleChange("customerId", value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <DropDownInput
            label="Courier Name"
            placeholder="Select Courier Name"
            options={courierOptions}
            value={filters.courierName} // ✅ Match filter state key
            onChange={(value) => handleChange("courierName", value)} // ✅ Match filter state key
          />

          <DropDownInput
            label="Channel Name"
            placeholder="Select Channel Name"
            options={channelOptions}
            value={filters.channelName}
            onChange={(value) => handleChange("channelName", value)}
          />
          <InputField
            label="AWBs"
            placeholder="AWBs Comma Separated"
            value={filters.awbId} // 🔄 Changed from filters.awbsID to filters.awbId
            onChange={(value) => handleChange("awbId", value)} // 🔄 Changed from "awbsID" to "awbId
          />
          <RadioInput
            label="Payment Mode"
            name="payment_mode"
            options={paymentOptions}
            selectedValue={filters.paymentMode}
            onChange={(value) => handleChange("paymentMode", value)}
          />
          <InputField
            label="Shipping ID"
            placeholder="Enter Shipping ID"
            value={filters.shippingId}
            onChange={(value) => handleChange("shippingId", value)}
          />
          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              className="h-12"
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </div>
        <MiniTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
        />
        {/* Bulk NDR Sheet */}

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}

        <DataTable
          columns={columns}
          tittle="NDR"
          data={finalFilteredData}
          showCheckbox={true}
          getSelectedRows={setSelectedRows}
          resetPage={resetPageCounter}
          isLoading={isLoading}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export NDR",
                filename: `NDR_${new Date().getTime()}`,
              },
            },
          ]}
        />
      </div>
      <AlertDialog />
      <ConfirmationModal />
    </SpaceWrapper>
  );
};

export default Ndr;
